/**
 * Wall: bars cut into halves, thirds, quarters and so on, stacked up so the
 * same width can be read off every row at once.
 *
 * Shade two quarters and the edge lands exactly where the half ends. Nobody
 * says "two quarters is a half" — the wall just does it, and the rows that
 * agree light up. Each row rings at its own pitch, which is the same fact
 * again: a half of a string sounds an octave above the whole.
 */

import { useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, RotateCcw } from 'lucide-preact';
import type { Spark } from '../../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { play, playRatio } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import type { Fraction } from '../../lib/wall.ts';
import { compare, equivalents, fractionName, fractionText, pitchRatio, valueOf, WALL_ROWS } from '../../lib/wall.ts';
import { Coach, ToyButton } from '../../ui/controls.tsx';

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

const TOP = 152;
const BOTTOM = 76;
const PAD_X = 18;
/** Room down the left for the row's name. */
const LABEL_W = 52;
/** Room down the right for the tick on a row that lines up. */
const TICK_W = 24;

const ROW_COLORS = [
  '--crayon-ink',
  '--crayon-purple',
  '--crayon-blue',
  '--crayon-sky',
  '--crayon-teal',
  '--crayon-green',
  '--crayon-lime',
  '--crayon-yellow',
  '--crayon-orange',
];

export function WallToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]);
  const layoutRef = useRef({ x: 0, w: 1, top: TOP, rowH: 30 });

  /** The shaded piece count for each row, indexed like WALL_ROWS. */
  const [filled, setFilled] = useState<number[]>(() => WALL_ROWS.map(() => 0));
  /** The row the readout is talking about. */
  const [picked, setPicked] = useState<number | null>(null);

  const live = useRef({ filled, picked });
  live.current = { filled, picked };

  const pickedFraction: Fraction | null =
    picked === null || filled[picked] === 0 ? null : { numerator: filled[picked], denominator: WALL_ROWS[picked] };
  const matches = pickedFraction ? equivalents(pickedFraction) : [];

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      updateSparks(sparksRef.current, dt);
      const state = live.current;

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      const x = PAD_X + LABEL_W;
      const w = width - x - PAD_X - TICK_W;
      const available = height - TOP - BOTTOM;
      const rowH = Math.min(46, available / WALL_ROWS.length);
      const top = TOP + Math.max(0, (available - rowH * WALL_ROWS.length) / 2);
      layoutRef.current = { x, w, top, rowH };

      const active = state.picked;
      const activeValue =
        active !== null && state.filled[active] > 0 ? state.filled[active] / WALL_ROWS[active] : null;
      const matching = new Set<number>();
      if (active !== null && activeValue !== null) {
        const f = { numerator: state.filled[active], denominator: WALL_ROWS[active] };
        for (const eq of equivalents(f)) matching.add(eq.denominator);
      }

      WALL_ROWS.forEach((denominator, row) => {
        const y = top + row * rowH;
        const barH = rowH - 6;
        const shaded = state.filled[row];
        const color = token(ROW_COLORS[row % ROW_COLORS.length]);
        const lit = matching.has(denominator);

        // The row's name, so the wall reads without a legend.
        ctx.save();
        ctx.fillStyle = lit ? token('--crayon-green') : token('--ink-soft');
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.font = `${lit ? 800 : 700} ${Math.min(15, rowH * 0.42)}px ui-rounded, system-ui, sans-serif`;
        ctx.fillText(denominator === 1 ? '1' : `1/${denominator}`, x - 10, y + barH / 2);
        ctx.restore();

        for (let piece = 0; piece < denominator; piece++) {
          const pw = w / denominator;
          const px = x + piece * pw;
          const on = piece < shaded;

          ctx.save();
          ctx.beginPath();
          ctx.roundRect(px + 1.5, y, pw - 3, barH, Math.min(7, barH * 0.28));
          ctx.fillStyle = on ? color : token('--paper-deep');
          ctx.fill();
          ctx.lineWidth = 2;
          ctx.strokeStyle = on ? 'rgba(44, 37, 69, 0.75)' : token('--paper-line');
          ctx.stroke();
          ctx.restore();
        }

        // A row that lands in the same place as the picked one gets a tick.
        if (lit && row !== active) {
          ctx.save();
          ctx.strokeStyle = token('--crayon-green');
          ctx.lineWidth = 3;
          ctx.lineCap = 'round';
          const cx = x + w + 9;
          ctx.beginPath();
          ctx.moveTo(cx, y + barH * 0.55);
          ctx.lineTo(cx + 4, y + barH * 0.78);
          ctx.lineTo(cx + 11, y + barH * 0.28);
          ctx.stroke();
          ctx.restore();
        }
      });

      // The line down the wall at the shaded edge. This is the whole toy: it
      // falls exactly on the end of every equivalent row.
      if (activeValue !== null) {
        const lineX = x + w * activeValue;
        ctx.save();
        ctx.strokeStyle = token('--crayon-green');
        ctx.lineWidth = 3;
        ctx.setLineDash([7, 6]);
        ctx.beginPath();
        ctx.moveTo(lineX, top - 8);
        ctx.lineTo(lineX, top + rowH * WALL_ROWS.length);
        ctx.stroke();
        ctx.restore();
      }

      drawSparks(ctx, sparksRef.current);
    },

    onDown: (p) => {
      const { x, w, top, rowH } = layoutRef.current;
      const row = Math.floor((p.pos.y - top) / rowH);
      if (row < 0 || row >= WALL_ROWS.length) return;
      if (p.pos.x < x - LABEL_W || p.pos.x > x + w) return;

      const denominator = WALL_ROWS[row];
      const piece = Math.floor(((p.pos.x - x) / w) * denominator);
      const clamped = Math.max(0, Math.min(denominator - 1, piece));
      // Tapping the piece you already ended on clears the row, so a child can
      // undo by tapping the same place twice.
      const next = live.current.filled[row] === clamped + 1 ? 0 : clamped + 1;

      const updated = [...live.current.filled];
      updated[row] = next;
      setFilled(updated);
      setPicked(next > 0 ? row : null);

      if (next > 0) {
        const f = { numerator: next, denominator };
        playRatio(pitchRatio(f), { duration: 0.5 });
        const found = equivalents(f);
        if (found.length > 0) {
          play('sparkle', 0.5);
          const canvas = canvasRef.current;
          const rect = canvas?.getBoundingClientRect();
          if (rect) {
            spawnSparks(sparksRef.current, { x: x + w * valueOf(f), y: top + rowH * row }, partyColors(), 14, 300);
          }
        }
      } else {
        play('pop', 0.4);
      }
    },
  });

  const clear = () => {
    setFilled(WALL_ROWS.map(() => 0));
    setPicked(null);
    play('whoosh', 0.6);
  };

  /** Two shaded rows are enough to say which is bigger. */
  const shadedRows = filled
    .map((n, row) => ({ numerator: n, denominator: WALL_ROWS[row] }))
    .filter((f) => f.numerator > 0);
  const biggest =
    shadedRows.length > 1 ? shadedRows.reduce((a, b) => (compare(a, b) >= 0 ? a : b)) : null;

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Fraction wall" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <ToyButton round label="Clear the wall" hideLabel onClick={clear}>
            <RotateCcw size={24} strokeWidth={2.5} />
          </ToyButton>
        </div>

        <p class="readout">
          {pickedFraction ? (
            <>
              <strong>{fractionText(pickedFraction)}</strong>
              <span>
                {matches.length > 0
                  ? `the same as ${matches.map(fractionText).join(', ')}`
                  : (fractionName(pickedFraction) ?? 'nothing else lines up')}
              </span>
            </>
          ) : (
            <>
              <strong>Shade a bar</strong>
              <span>tap any piece</span>
            </>
          )}
        </p>

        <Coach>
          {biggest && shadedRows.length > 1
            ? `${fractionText(biggest)} is the biggest one shaded`
            : 'Shade two rows to see which is bigger'}
        </Coach>
      </div>

      <div class="dock">{tabs}</div>
    </div>
  );
}
